/**
 * @file billStatus.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-05
 * @description A bill's status and Queried, in words, for the Status window
 *              (BillStatusDialog) and the row actions. Mirrors the server's
 *              rules (utils/billStatusRules.js): Queried is always Draft; a
 *              change on a bill entered here that is in Xero goes to Xero —
 *              the server judges by what Xero says NOW, so this is what to
 *              expect, and the server has the last word.
 *
 * 1.1.0 (2026-10-05, Jade's feedback): the words a bill's status is SHOWN in —
 *              Draft, Approved, Paid (billStatusLabel) — everywhere a bill's
 *              status is on screen. Display only: the stored values (Draft,
 *              Submitted, Authorised, Paid, Voided, Deleted) and what goes to
 *              Xero are unchanged. A new bill offers Approved or Draft only
 *              (BILL_STATUS_CHOICES).
 */

/** Stored status → the word shown. Submitted (awaiting approval) is not approved, so it reads Draft. */
const STATUS_LABELS = {
  DRAFT: 'Draft',
  SUBMITTED: 'Draft',
  AUTHORISED: 'Approved',
  PAID: 'Paid',
  VOIDED: 'Voided',
  DELETED: 'Deleted',
};

/**
 * A bill's status as shown: Draft, Approved, Paid (Voided and Deleted as
 * themselves). Anything unknown is shown as it is.
 * @param {string} status — the stored value, any case
 */
export function billStatusLabel(status) {
  const raw = status == null ? '' : String(status).trim();
  return STATUS_LABELS[raw.toUpperCase()] || raw;
}

/** What a new bill can be saved as (value stored → label shown). Queried still forces Draft. */
export const BILL_STATUS_CHOICES = Object.freeze([
  Object.freeze({ value: 'Authorised', label: 'Approved' }),
  Object.freeze({ value: 'Draft', label: 'Draft' }),
]);

/** A real Xero id (not a TEMP-/LOCAL- placeholder). */
export const billInXero = (b) => !!(b && b.xero_bill_id && String(b.xero_bill_id).trim()
  && !/^(TEMP|LOCAL)-/i.test(String(b.xero_bill_id)));

const DEAD = ['voided', 'deleted', 'paid'];

/**
 * Can this bill's status be changed here, and if not why (for a disabled
 * button's title)?
 * @returns {{ ok: boolean, reason: string|null }}
 */
export function statusChangeAllowed(b) {
  if (!b) return { ok: false, reason: 'No bill' };
  const st = String(b.status || '').toLowerCase();
  if (DEAD.includes(st)) return { ok: false, reason: `This bill is ${st}` };
  if (b.source !== 'local') return { ok: false, reason: 'Keyed in Xero — change its status in Xero' };
  if (Math.abs(parseFloat(b.amount_paid || 0)) > 0.005) return { ok: false, reason: 'It has payments against it' };
  const t = String(b.transaction_type || 'BILL').toUpperCase();
  if (t === 'OVERPAYMENT' || t === 'PREPAYMENT') return { ok: false, reason: 'Overpayments and prepayments have no status here' };
  return { ok: true, reason: null };
}

/**
 * What saving `to` will do, in a sentence.
 * @param {object} b — the bill: status, is_queried, xero_bill_id, source
 * @param {{ status: string, queried: boolean }} to
 * @returns {{ text: string, blocked: boolean, unchanged: boolean }}
 */
export function billStatusOutcome(b, to) {
  const fromStatus = b?.status === 'Draft' || b?.status === 'Submitted' ? b.status : 'Authorised';
  const fromQueried = !!b?.is_queried;
  const target = to.queried ? 'Draft' : to.status;
  const unchanged = target === fromStatus && !!to.queried === fromQueried;
  const inXero = billInXero(b);

  if (!inXero) {
    if (unchanged) return { text: '', blocked: false, unchanged: true };
    return {
      text: `Not in Xero yet: it goes to Xero as ${target === 'Draft' ? 'a draft' : 'an approved bill'}${to.queried ? ' (queried bills always go as drafts)' : ''}.`,
      blocked: false, unchanged: false,
    };
  }
  if (unchanged) {
    return {
      text: 'No change here. Saving checks that Xero has the same status, and puts Xero right if it does not.',
      blocked: false, unchanged: false,
    };
  }
  if (fromStatus === 'Authorised' && !fromQueried) {
    if (to.queried) {
      return {
        text: 'Xero has this bill approved. Querying it VOIDS it in Xero and sends it again as a new draft (refused if it has payments against it or its date is locked). A note in Xero says who queried it.',
        blocked: false, unchanged: false,
      };
    }
    if (target === 'Draft') {
      return {
        text: 'Xero cannot take an approved bill back to draft. Mark it Queried instead: it is voided in Xero and sent again as a draft.',
        blocked: true, unchanged: false,
      };
    }
  }
  if (target === 'Authorised') {
    return { text: 'Approved in Xero too. A note in Xero says who approved it.', blocked: false, unchanged: false };
  }
  return {
    text: `Stays a draft in Xero.${to.queried !== fromQueried ? ` A note in Xero records that it was ${to.queried ? 'queried' : 'cleared'}.` : ''}`,
    blocked: false, unchanged: false,
  };
}
